import InputField from 'components/Shared/Fields'
import Text from 'particles/Text'
import { useFormikContext } from 'formik'
import React, { useCallback } from 'react'
import { useNewTranslation } from 'hooks'
import styled from 'particles/themes/styled'
import { theme } from 'theme'
import BaseDivider from '../../PredefinedTaskEditor/BaseDivider'
import { EditorContentCardContainer } from '../../PredefinedTaskEditor/styles'
import { AddBlockButton, BlockEditor } from '../../Shared/Blocks'
import { EditorValues } from '../types'
import { BLOCK_TYPE, CustomStepBlock } from 'state/tasks'
import { BLOCK_TYPE as REGISTRATION_BLOCK_TYPE } from 'state/registrations'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'
import { reorderList } from 'components/utils'
import { InputFieldContainer } from 'components/Shared/Fields/styles'
import _ from 'lodash'
import { BLOCK_GROUP } from '../../Shared/Blocks/BlockMenu'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import { fieldIdentifiers } from 'components/Shared/Comment'
import { generateUuid } from 'services/uuid'

const StepEditorContainer = styled(EditorContentCardContainer as any)`
    max-width: 594px;

    & .blocks-area {
        display: flex;
        flex-direction: column;
        padding: ${theme.newSpacing.xl} 0;
        margin-bottom: -${theme.newSpacing.s};
    }
`

type StepEditorProps = {
    className?: string
    stepIndex: number
    campaignStatus?: CAMPAIGN_STATUS_ENUM
    stepUuid: string
    showBrief?: boolean
}
const StepEditor: React.FC<StepEditorProps> = ({ className, stepIndex, campaignStatus, stepUuid, showBrief }) => {
    const [t] = useNewTranslation()
    const stepNumber = stepIndex + 1

    return (
        <StepEditorContainer className={className}>
            <div className="step-title">
                <Text scale="bodyL700" color="primary">
                    {t('campaign.section.stepNumber', {
                        stepNumber,
                    })}
                </Text>
                <Text scale="bodyS400" color="secondary">
                    {t('campaignEditor.createTask.enterInstructionCustomTaskSubtext', {
                        stepNumber,
                    })}
                </Text>
            </div>
            <CampaignEditorInputFieldContainer>
                <InputField.TextInputField
                    label={t('campaignEditor.createTask.enterStepTitleHeader')}
                    name={`steps[${stepIndex}].data.title`}
                    isRequired
                    width={'100%'}
                    showInputLength
                    isReadOnly={showBrief}
                    maxInputLength={150}
                    identifier={`${stepUuid}_${fieldIdentifiers.stepTitle}`}
                />
            </CampaignEditorInputFieldContainer>
            <BaseDivider />

            <BlocksArea stepIndex={stepIndex} campaignStatus={campaignStatus} showBrief={showBrief} />
        </StepEditorContainer>
    )
}

function createNewBlock(type: CustomStepBlock['type']): CustomStepBlock {
    const initialValue = {
        uuid: generateUuid(),
        type,
        title: '',
    }

    switch (type) {
        case BLOCK_TYPE.TEXT:
            return {
                ...initialValue,
                description: '',
            } as CustomStepBlock
        case BLOCK_TYPE.PHOTO_CAROUSEL:
            return {
                ...initialValue,
                description: '',
                files: [],
                allow_download: false,
            } as CustomStepBlock
        case BLOCK_TYPE.PHOTO_SUBMISSION:
            return {
                ...initialValue,
                max_photos: 3,
            } as CustomStepBlock
        case BLOCK_TYPE.TEXT_SUBMISSION:
        case BLOCK_TYPE.URL_SUBMISSION:
            return initialValue as CustomStepBlock
        default:
            return {
                ...initialValue,
                description: '',
            } as CustomStepBlock
    }
}

interface BlocksEditorProps {
    stepIndex: number
    campaignStatus?: CAMPAIGN_STATUS_ENUM
    showBrief?: boolean
}
const BlocksArea: React.FC<BlocksEditorProps> = ({ stepIndex, campaignStatus, showBrief }) => {
    const [t] = useNewTranslation()
    const { values: formikValues, setValues, setFieldTouched } = useFormikContext<EditorValues>()

    const handleAddBlock = useCallback((_blockType: BLOCK_TYPE | REGISTRATION_BLOCK_TYPE): void => {
        const blockType = _blockType as BLOCK_TYPE
        setValues(currentValues => {
            const newValues = { ...currentValues }
            const newBlock = createNewBlock(blockType)
            newValues.steps[stepIndex].data.blocks.push(newBlock)
            return newValues
        })
    }, [])

    const handleRemoveBlock = useCallback((blockIndex: number): void => {
        setValues(currentValues => {
            const newValues = { ...currentValues }
            newValues.steps[stepIndex].data.blocks.splice(blockIndex, 1)
            return newValues
        })

        setFieldTouched(`steps[${stepIndex}].data.blocks` as never, true, true)
    }, [])

    const onDragEnd = useCallback((result: DropResult): void => {
        setValues(currentValues => {
            if (!result.destination || result.destination.index === result.source.index) {
                return currentValues
            }

            const newValues = { ...currentValues }
            newValues.steps[stepIndex].data.blocks = reorderList(
                newValues.steps[stepIndex].data.blocks,
                result.source.index,
                result.destination.index,
            )
            return newValues
        })
    }, [])

    return (
        <CampaignEditorInputFieldContainer>
            {(isReadOnly: boolean) => (
                <InputFieldContainer>
                    <Text scale="bodyM500" className="field-title">
                        {t('campaignEditor.createTask.selectFieldForStepHeader')}
                    </Text>
                    <Text scale="bodyS400" color="secondary" className="field-sub-title">
                        {t('campaignEditor.createTask.selectFieldForStepSubtext')}
                    </Text>

                    {!_.isEmpty(formikValues.steps[stepIndex].data.blocks) && (
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="droppable">
                                {(provided): React.ReactElement => (
                                    <div className="blocks-area" ref={provided.innerRef} {...provided.droppableProps}>
                                        {(formikValues.steps[stepIndex].data.blocks || []).map(
                                            (block: CustomStepBlock, index: number) => (
                                                <Draggable key={index} index={index} draggableId={index.toString()}>
                                                    {(draggableProvided): JSX.Element => (
                                                        <BlockEditor
                                                            type={block.type}
                                                            namePrefix={`steps[${stepIndex}].data.blocks[${index}]`}
                                                            blockIndex={index}
                                                            hideRemove={campaignStatus === CAMPAIGN_STATUS_ENUM.LIVE}
                                                            onRemove={handleRemoveBlock}
                                                            ref={draggableProvided.innerRef}
                                                            draggableProvided={draggableProvided}
                                                            blockUuid={block.uuid}
                                                            isReadOnly={isReadOnly || showBrief}
                                                            {...draggableProvided.draggableProps}
                                                        />
                                                    )}
                                                </Draggable>
                                            ),
                                        )}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}

                    {!isReadOnly && !showBrief && campaignStatus !== CAMPAIGN_STATUS_ENUM.LIVE ? (
                        <AddBlockButton onSelected={handleAddBlock} blockGroup={BLOCK_GROUP.CUSTOM} />
                    ) : null}
                </InputFieldContainer>
            )}
        </CampaignEditorInputFieldContainer>
    )
}

export default StepEditor
